/** One role as a pill row: who it is, what kind, how many hold it, how much of
 *  the CRM it opens - and the menu that selects it or copies its key. */
import { Copy, MoreHorizontal, SlidersHorizontal } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { Avatar, StatusPill, useToast } from "../../../ui";
import { Pop, useClosePops } from "../../../shell/Pop";
import { ROLE_COLS, roleKind, roleLabel, type RoleRow } from "./types";

export function RoleListRow({
  role,
  locale,
  active,
  rights,
  onSelect,
}: {
  role: RoleRow;
  locale: string;
  /** This is the role whose rights the card below is showing. */
  active: boolean;
  /** Already formatted - the list cannot count a role's grants on its own. */
  rights: string;
  onSelect: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  const kind = roleKind(role);
  const label = roleLabel(role, locale);

  async function copyKey() {
    close();
    try {
      await navigator.clipboard.writeText(role.key);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  return (
    // `relative`, because the name button stretches an ::after over the whole
    // row so the row selects end to end. The menu then has to sit on top of that
    // overlay or its clicks never reach it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: ROLE_COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        {/* `brand` is the tone for the one record a screen is actually about,
            which is exactly what selecting a role here means. */}
        <Avatar name={label} tone={active ? "brand" : "soft"} />
        <span className="min-w-0">
          <button
            type="button"
            aria-pressed={active}
            onClick={onSelect}
            className="crm-prow-n crm-trunc block w-full text-left after:absolute after:inset-0 after:rounded-full"
          >
            {label}
          </button>
          <span className="crm-prow-s crm-trunc block font-mono">{role.key}</span>
        </span>
      </span>

      <span>
        <StatusPill tone={kind.tone}>{t(kind.key)}</StatusPill>
      </span>

      <span className="crm-tnum text-[13px]">{role.memberCount}</span>
      <span className="crm-tnum text-[13px] font-bold">{rights}</span>

      <span className="relative z-[1] justify-self-end">
        <Pop
          trigger={(open) => (
            <button
              type="button"
              className={`crm-cb${open ? " is-on" : ""}`}
              title={t("common.more")}
              aria-label={t("common.more")}
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <MoreHorizontal className="size-4" />
            </button>
          )}
        >
          <button
            type="button"
            className="crm-mi"
            onClick={() => {
              close();
              onSelect();
            }}
          >
            <SlidersHorizontal className="size-4" />
            {t("roles.edit")}
          </button>
          <button type="button" className="crm-mi" onClick={() => void copyKey()}>
            <Copy className="size-4" />
            {t("roles.copyKey")}
          </button>
        </Pop>
      </span>
    </div>
  );
}
